import { gql } from '@apollo/client';

export const STOP_FIELDS = gql`
    fragment StopFields on Stop {
        id
        name
        stationId
        __typename
        type
        status
        asynchronousChildren
    }
`;

export const ROUTE_FIELDS = gql`
    fragment RouteFields on Route {
        id
        name
        status
        track
        __typename
        totalStops
        actionLog {
            id
            action
            timestamp
            userId
            userEmail
            stop {
                id
                name
            }
        }
        stops {
            ...StopFields
        }
    }
    ${STOP_FIELDS}
`;

// Lighter fragment for list/search views - excludes heavy stops and track fields
export const ROUTE_LIST_FIELDS = gql`
    fragment RouteListFields on Route {
        id
        name
        status
        __typename
        totalStops
        actionLog {
            id
            action
            timestamp
            userId
            userEmail
            stop {
                id
                name
            }
        }
    }
`;

export const GET_ROUTE = gql`
    query GetRoute($id: ID!) {
        getRoute(id: $id) {
            ...RouteFields
        }
    }
    ${ROUTE_FIELDS}
`;

export const GET_STOP = gql`
    query GetStop($routeId: ID!, $stopId: ID!) {
        getStop(routeId: $routeId, stopId: $stopId) {
            id
            name
            stationId
            type
            status
            asynchronousChildren
            station {
                id
                name
            }
            route {
                id
                name
                status
            }
            actionLog {
                id
                action
                timestamp
                userId
                userEmail
                stop {
                    id
                    name
                }
            }
        }
    }
`;

export const LIST_ROUTES = gql`
    query ListRoutes {
        listRoutes {
            ...RouteFields
        }
    }
    ${ROUTE_FIELDS}
`;

export const GET_STATION = gql`
    query GetStation($id: ID!) {
        getStation(id: $id) {
            id
            __typename
            name
            description
            remotePortalId
        }
    }
`;

export const GET_STATION_WITH_ACTIVE_ROUTES = gql`
    query GetStationWithActiveRoutes($id: ID!) {
        getStationWithActiveRoutes(id: $id) {
            id
            name
            description
            remotePortalId
            stats {
                totalCurrentlyStationed
                totalPending
                totalCompleted
                totalActiveRoutes
                totalPausedRoutes
            }
            activeRoutes {
                route {
                    id
                    name
                    status
                }
                stop {
                    id
                    name
                    status
                    stationId
                }
                action {
                    id
                    action
                    timestamp
                    userId
                    userEmail
                }
            }
        }
    }
`;

export const BUILD_ROUTE = gql`
    query ProcessInstructions($input: ProcessInstructionsInput!) {
        processInstructions(input: $input) {
            id
            name
            track
            stops {
                id
                name
                stationId
                type
                status
                asynchronousChildren
            }
        }
    }
`;

export const LIST_STATIONS = /* GraphQL */ gql(`
    query listStations {
        listStations {
            id
            name
            description
            remotePortalId
        }
    }
`);

export const LIST_ROUTE_TEMPLATES = /* GraphQL */ gql(`
    query listRouteTemplates {
        listRouteTemplates {
            id
            name
            stops {
                id
                name
                type
                stationId
                asynchronousChildren
            }
            track
            description
        }
    }
`);

export const GET_ROUTE_TEMPLATE = gql`
    query GetRouteTemplate($id: ID!) {
        getRouteTemplate(id: $id) {
            id
            name
            stops {
                id
                name
                type
                stationId
                asynchronousChildren
            }
            track
            description
        }
    }
`;

export const LIST_INVOICES = /* GraphQL */ `
    query listInvoices {
        listInvoices {
            id
            planId
            periodStart
            periodEnd
            description
            pdfUrl
            number
            startingBalance
        }
    }
`;

export const GET_TENANT = /* GraphQL */ `
    query getTenant {
        getTenant {
            id
            planId
            trialPeriodDays
            trialPeriodTimestamp
            cancelPlanAt
            deleteAccountFlag
            plan {
                id
                title
                price
                statementDescriptor
                featureList
                totalUsers
                totalActiveRoutes
            }
            trialPeriod {
                trialPeriodStatus
            }
        }
    }
`;

export const PREVIEW_PLAN_CANCEL = /* GraphQL */ `
    query previewPlanCancel {
        getTenant {
            id
            planId
            trialPeriodDays
            trialPeriodTimestamp
            cancelPlanAt
            plan {
                id
                title
                price
                statementDescriptor
                featureList
                totalUsers
                totalActiveRoutes
            }
            trialPeriod {
                trialPeriodStatus
            }
        }
        getUpcomingInvoice {
            nextInvoiceTimestamp
        }
    }
`;

export const LIST_USERS = /* GraphQL */ `
    query listUsers {
        listUsers {
            email
            enabled
            id
            group
            created
            modified
            status
        }
    }
`;

export const GET_USER = /* GraphQL */ `
    query getUser($id: String!) {
        getUser(id: $id) {
            email
            enabled
            id
            group
            created
            modified
            status
        }
    }
`;

export const LIST_CHARGES = /* GraphQL */ `
    query listCharges($input: ListChargesInput, $limit: Int) {
        listCharges(input: $input, limit: $limit) {
            id
            created
            amount
            paid
            paymentType
            last4
            refunded
            refundedAmount
            failureMessage
        }
    }
`;

export const GET_CURRENT_AND_ALL_PLANS = /* GraphQL */ `
    query getCurrentAndAllPlans {
        listPlans {
            id
            title
            price
            statementDescriptor
            featureList
            totalUsers
        }
        getTenant {
            id
            planId
            trialPeriodDays
            trialPeriodTimestamp
            cancelPlanAt
            plan {
                id
                price
                statementDescriptor
                featureList
                totalUsers
                totalActiveRoutes
            }
            trialPeriod {
                trialPeriodStatus
            }
        }
    }
`;

export const LIST_PAYMENT_METHODS = /* GraphQL */ `
    query listPaymentMethods {
        listPaymentMethods {
            id
            paymentType
            expirationDate
            last4
            cardType
            default
        }
    }
`;

export const LIST_PLANS = /* GraphQL */ `
    query listPlans {
        listPlans {
            id
            title
            price
            statementDescriptor
            featureList
            totalUsers
        }
    }
`;

export const CHECKOUT = /* GraphQL */ `
    query checkout($id: String!) {
        checkout(planId: $id) {
            nextInviceSubTotal
            newPlanTotal
            oldPlanTotal
            newPlanTotalRemaining
            oldPlanTotalUnused
            nextInvoiceTimestamp
            previousInvoiceTimestamp
        }
    }
`;

// Search queries
export const SEARCH_STATIONS = gql`
    query SearchStations($input: SearchStationsInput!) {
        searchStations(input: $input) {
            items {
                id
                name
                description
                remotePortalId
            }
        }
    }
`;

export const SEARCH_TEMPLATES = gql`
    query SearchTemplates($input: SearchTemplatesInput!) {
        searchTemplates(input: $input) {
            items {
                id
                name
                stops {
                    id
                    name
                    type
                    stationId
                    asynchronousChildren
                }
                track
                description
            }
        }
    }
`;

export const SEARCH_ROUTES = gql`
    query SearchRoutes($input: SearchRoutesInput!) {
        searchRoutes(input: $input) {
            items {
                ...RouteListFields
            }
        }
    }
    ${ROUTE_LIST_FIELDS}
`;

export const CHECK_EMAIL_EXISTS = /* GraphQL */ `
    query checkEmailExists($email: AWSEmail!) {
        checkEmailExists(email: $email) {
            exists
        }
    }
`;
